import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import axiosService from '../../inits/axios';
import { toast } from 'react-toastify';

import FaqList from '../../components/Faqs/Faqs';
import PageLoader from '../../components/Common/PageLoader';

import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

import '../../styles/piece.css';

const AddCategorySchema = Yup.object().shape({
  name: Yup.string().trim().required("Can't add empty Category"),
});

const FaqsContainer = () => {
  const mountedRef = useRef(true);

  // Redux state
  const categoriesFromStore = useSelector((state) => state.auth.categories);

  // Local state
  const [isFetching, setIsFetching] = useState(true);
  const [faqs, setFaqs] = useState(null);
  const [categories, setCategories] = useState(null); // from API for dropdown
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [mode, setMode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  // Derive access mode from Redux categories
  const accessMode = useMemo(() => {
    try {
      const faqCategory = categoriesFromStore.find((c) => c.link === 'faqs');
      return faqCategory?.mode || '';
    } catch {
      return '';
    }
  }, [categoriesFromStore]);

  // Fetch categories from API
  const getCategories = async () => {
    try {
      const data = await axiosService.get('/categories');
      if (mountedRef.current) setCategories(data);
    } catch {
      /* no-op */
    }
  };

  // Fetch FAQs from API
  const getFaqs = async () => {
    try {
      const data = await axiosService.get('/faqs');
      if (mountedRef.current) setFaqs(data);
    } catch {
      /* no-op */
    }
  };

  // Delete FAQ
  const deleteFaq = async (faq) => {
    await axiosService.delete('/faqs/' + faq.id);
    // Refresh after delete
    await getFaqs();
  };

  // Remove category
  const removeCategory = async () => {
    if (!selectedCategoryId) return;
    try {
      await axiosService.delete('/categories/' + selectedCategoryId);
      toast.success('Selected category removed');
      setSelectedCategoryId('');
      await getCategories();
    } catch (e) {
      toast.error('Failed to remove category');
    }
  };

  // Initialize on mount
  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      await Promise.all([getFaqs(), getCategories()]);
      setIsFetching(false);
    })();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Update access when accessMode changes
  useEffect(() => {
    if (accessMode) {
      setMode(accessMode);
      setAccessDenied(false);
    } else {
      setAccessDenied(true);
    }
  }, [accessMode]);

  // Loading state
  if (isFetching) return <PageLoader />;

  // Access denied state
  if (accessDenied) return <div>This category is not accessible to the logged in user.</div>;

  return (
    <div className="box">
      <div className="box-header with-border">
        <h3 className="box-title">Faqs</h3>
        {mode === 'edit' && (
          <Link className="cus-btn-primary pull-right" to="/faq/create">
            Add Faq
          </Link>
        )}
      </div>

      <div className="box-body">
        {mode === 'edit' && (
          <div
            style={{
              background: '#f8f9fb',
              border: '1px solid #e3e6ec',
              borderRadius: 8,
              padding: '18px 20px',
              margin: '16px 0 24px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 28,
              alignItems: 'flex-start',
            }}
          >
            {/* Add new category */}
            <div style={{ flex: '1 1 300px', minWidth: 260 }}>
              <label
                style={{
                  display: 'block',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  marginBottom: 6,
                  color: '#444',
                }}
              >
                Add New Category
              </label>
              <Formik
                initialValues={{ name: '' }}
                validationSchema={AddCategorySchema}
                onSubmit={async (values, { resetForm, setSubmitting }) => {
                  try {
                    const payload = { name: values.name.trim().toLowerCase() };
                    const category = await axiosService.post('/categories', payload);

                    if (category?.name?.toLowerCase() === payload.name) {
                      toast.success('New category ' + category.name + ' added');
                      resetForm();
                      await getCategories();
                    } else {
                      toast.error('Unable to add category');
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.message || 'Failed to add category');
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({ isSubmitting }) => (
                  <Form className="form">
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Field
                        name="name"
                        placeholder="e.g. billing"
                        className={`form-control ${mode === 'view' ? 'disabled' : ''}`}
                        type="text"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="submit"
                        className="cus-btn-primary"
                        disabled={isSubmitting || mode === 'view'}
                        style={{ margin: 0, whiteSpace: 'nowrap' }}
                      >
                        {isSubmitting ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                    <div
                      className="text-danger"
                      style={{ marginTop: 4, fontSize: '0.8rem', minHeight: 18 }}
                    >
                      <ErrorMessage name="name" />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>

            {/* Manage existing categories */}
            {categories && (
              <div style={{ flex: '1 1 300px', minWidth: 260 }}>
                <label
                  style={{
                    display: 'block',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    marginBottom: 6,
                    color: '#444',
                  }}
                >
                  Categories
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    className="form-control"
                    style={{ flex: 1 }}
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                  >
                    <option value="">View all Categories</option>
                    {categories.map((faqCategory, i) => (
                      <option value={faqCategory.id} key={i}>
                        {faqCategory.name}
                      </option>
                    ))}
                  </select>
                  {selectedCategoryId && (
                    <button
                      className="cus-btn-danger"
                      onClick={removeCategory}
                      style={{ margin: 0, whiteSpace: 'nowrap' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div style={{ minHeight: 18 }} />
              </div>
            )}
          </div>
        )}

        {faqs ? <FaqList mode={mode} faqs={faqs} deleteFaq={deleteFaq} /> : ''}
      </div>
    </div>
  );
};

export default FaqsContainer;
